import {
  Box,
  Button,
  IconButton,
  Stack,
  SxProps,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, RefObject } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDialog } from '@/logic/state';

import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { DateTime } from '@/utility/luxon';
import { ILocale } from '@/lang/types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MyTransactions from '../transactions/my-transactions';

import { checkIsRtl } from '@/lang/utils';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useRouter } from 'next/router';

import ImageBoxFuture from '@/components/shared/image-box-future';
import { useUserBalance } from '@/features/wallet/hooks/use-user-balance';
import { Transaction } from '@/features/wallet/services/types';

const WalletExpiryDateIcon = '/icons/profile/wallet-expiry-date.svg';

interface CashbackInfoProps {
  myCashbackRef: RefObject<HTMLDivElement>;
  accordionSx: SxProps;
  accordionBoxIconSx: SxProps;
  transactions: Transaction[];
  isFetchingTransactions: boolean;
  isErrorTransactions: boolean;
  refetchTransactions: () => void;
}

export const CashbackInfo: FC<CashbackInfoProps> = ({
  myCashbackRef,
  accordionSx,
  accordionBoxIconSx,
  transactions,
  isFetchingTransactions,
  isErrorTransactions,
  refetchTransactions,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const { locale } = useRouter();

  const setDialog = useDialog(s => s.setDialog, shallow);
  const isRtl = checkIsRtl(locale as ILocale);

  const { userFormattedCashbackBalance, userNextExpiredCashbackTransaction } =
    useUserBalance();

  return (
    <>
      <Box
        ref={myCashbackRef}
        sx={{
          background: theme.palette.common.white,
          boxShadow: theme.customShadows.cardShadow,
          borderRadius: '16px',
          p: { tablet: pxToRem(32), mobile: '22px' },
          pl: { tablet: pxToRem(20), mobile: '17px' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography
              sx={{
                color: theme.palette.grey[50],
                fontSize: pxToRem(16),
                fontWeight: '400',
                lineHeight: '150%',
              }}
            >
              <FormattedMessage id='cashback.balance' />
            </Typography>
            <Typography
              sx={{
                color: theme.palette.grey[80],
                fontSize: pxToRem(22),
                fontWeight: '700',
                lineHeight: '150%',
              }}
            >
              {userFormattedCashbackBalance}&nbsp;
              <FormattedMessage id='currency.SAR' />
            </Typography>
          </Box>
        </Box>
        {userNextExpiredCashbackTransaction && (
          <Button
            sx={{
              marginTop: '16px',
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
            onClick={() =>
              setDialog({
                wrapper: true,
                isWalletExpiryDetailsDialog: true,
                WalletExpiryDetailsDialogPayload: {
                  isCashback: true,
                },
              })
            }
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box
                component='div'
                sx={{
                  ...accordionBoxIconSx,
                  backgroundColor: theme.palette.primary.main,
                  width: pxToRem(46),
                  height: pxToRem(46),
                }}
              >
                <ImageBoxFuture
                  img={WalletExpiryDateIcon}
                  title={intl.formatMessage({
                    id: 'alt.my_wallet.expiry.date',
                  })}
                  objectFit='contain'
                  sx={{
                    width: { tablet: pxToRem(33), mobile: '40px' },
                    height: { tablet: pxToRem(33), mobile: '24px' },
                  }}
                />
              </Box>

              <Box
                sx={{
                  textAlign: 'left',
                }}
              >
                <Typography
                  component='span'
                  sx={{
                    fontSize: pxToRem(16),
                    fontWeight: '400',
                    color: theme.palette.grey[50],
                  }}
                >
                  <FormattedMessage id='wallet.expiry_details.title' />
                </Typography>
                <Stack
                  direction='row'
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    component='span'
                    sx={{
                      fontSize: pxToRem(18),
                      fontWeight: '700',
                      color: theme.palette.grey[80],
                    }}
                  >
                    {userNextExpiredCashbackTransaction.restAmount.toFixed?.(2) ??
                      (0).toFixed(2)}
                  </Typography>
                  <Typography
                    component='span'
                    sx={{
                      fontSize: pxToRem(16),
                      fontWeight: '400',
                      color: theme.palette.grey[80],
                    }}
                  >
                    &nbsp; <FormattedMessage id='currency.SAR' /> | &nbsp;
                    {DateTime.fromISO(userNextExpiredCashbackTransaction.expireAt, {
                      locale,
                    }).toFormat('d MMM')}
                  </Typography>
                </Stack>
              </Box>
            </Box>

            <IconButton
              sx={{
                width: '24px',
                height: '24px',
              }}
            >
              <ArrowForwardIosIcon
                sx={{
                  color: theme.palette.primary.main,
                  transform: isRtl && 'scaleX(-1)',
                }}
              />
            </IconButton>
          </Button>
        )}
        <Box
          onClick={() =>
            setDialog({
              wrapper: true,
              isAboutCashbackDialog: true,
            })
          }
          sx={{
            marginTop: '16px',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            width: 'fit-content',
          }}
        >
          <InfoOutlinedIcon sx={{ color: theme.palette.primary.main }} />
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: pxToRem(16),
              fontWeight: '700',
              marginInlineStart: '8px',
            }}
          >
            <FormattedMessage id='cashback.learn.more' />
          </Typography>
        </Box>
      </Box>

      <MyTransactions
        accordionSx={accordionSx}
        transactions={transactions}
        isFetching={isFetchingTransactions}
        isErrorTransactions={isErrorTransactions}
        refetchTransactions={() => refetchTransactions()}
      />
    </>
  );
};
